import { useDraggable, useDroppable } from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  FOLDER_NAME_CONFLICT_MESSAGE,
  type Project,
  type ProjectFolder,
  normalizeFolderName,
} from '@terafile/shared'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ApiError } from '../lib/api.js'
import { FETCH_ERROR_FALLBACK, SUBMIT_ERROR_FALLBACK, toUserMessage } from '../lib/error-copy.js'
import { listFiles } from '../lib/files-api.js'
import { deleteFolder, listFolders } from '../lib/folders-api.js'
import { getProject, updateProject } from '../lib/projects-api.js'
import { DROP_UNCATEGORIZED } from '../lib/projects-location.js'
import { useToast } from '../lib/toast.js'
import { DeleteFolderModal } from './DeleteFolderModal.js'

type Props = {
  projects: Project[]
  onMoveToTrash: (id: string, name: string) => void
  archivePending: boolean
  onCreateInFolder: (folderId: string | null) => void
  creatingFolder?: boolean
  newFolderName?: string
  onStartCreateFolder?: () => void
  onCancelCreateFolder?: () => void
  onChangeNewFolderName?: (v: string) => void
  onSubmitCreateFolder?: () => void
  createFolderPending?: boolean
  showFolderToolbar?: boolean
  /** サーバー側 CONFLICT など、作成フォーム直下に出すエラー */
  createNameError?: string | null
}

/**
 * 稼働中タブ「すべて」の1階層エクスプローラー。
 * DnD の Context は ProjectsDndShell 側。ここは droppable / draggable のみ。
 */
export function ActiveProjectsExplorer({
  projects,
  onMoveToTrash,
  archivePending,
  onCreateInFolder,
  creatingFolder = false,
  newFolderName = '',
  onStartCreateFolder,
  onCancelCreateFolder,
  onChangeNewFolderName,
  onSubmitCreateFolder,
  createFolderPending = false,
  showFolderToolbar = true,
  createNameError = null,
}: Props) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null)

  const foldersQ = useQuery({
    queryKey: ['folders'],
    queryFn: listFolders,
  })

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['projects'] })
    queryClient.invalidateQueries({ queryKey: ['folders'] })
  }

  const moveMutation = useMutation({
    mutationFn: ({ id, folderId }: { id: string; folderId: string | null }) =>
      updateProject(id, { folderId }),
    onSuccess: () => {
      invalidate()
      toast.success('移動しました')
    },
    onError: (err) => toast.error(toUserMessage(err, SUBMIT_ERROR_FALLBACK)),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteFolder(id),
    onSuccess: () => {
      invalidate()
      setDeleteTarget(null)
      toast.success('フォルダを削除しました（中のプロジェクトは未分類です）')
    },
    onError: (err) => toast.error(toUserMessage(err, SUBMIT_ERROR_FALLBACK)),
  })

  const folders = foldersQ.data?.items ?? []
  const grouped = useMemo(() => groupProjects(projects, folders), [projects, folders])
  const toggle = (key: string) => setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }))

  if (foldersQ.isLoading) {
    return <p className="text-sm text-muted-foreground">フォルダを読み込み中…</p>
  }

  const foldersUnavailable = !!foldersQ.error

  return (
    <div className="space-y-3">
      {foldersUnavailable && <FoldersUnavailableBanner error={foldersQ.error} />}

      {showFolderToolbar && !foldersUnavailable && onStartCreateFolder && (
        <FolderToolbar
          creating={creatingFolder}
          newFolderName={newFolderName}
          folderCount={folders.length}
          existingNames={folders.map((f) => f.name)}
          nameError={createNameError}
          onStartCreate={onStartCreateFolder}
          onCancelCreate={() => onCancelCreateFolder?.()}
          onChangeName={(v) => onChangeNewFolderName?.(v)}
          onSubmitCreate={() => onSubmitCreateFolder?.()}
          createPending={createFolderPending}
        />
      )}

      <div className="space-y-1">
        {!foldersUnavailable &&
          grouped.folders.map(({ folder, items }) => (
            <FolderSection
              key={folder.id}
              dropId={folder.id}
              title={folder.name}
              count={items.length}
              collapsed={!!collapsed[folder.id]}
              onToggle={() => toggle(folder.id)}
              onDelete={() => setDeleteTarget({ id: folder.id, name: folder.name })}
              onCreateHere={() => onCreateInFolder(folder.id)}
            >
              {items.map((p) => (
                <ActiveProjectRow
                  key={p.id}
                  project={p}
                  folders={folders}
                  onMoveToTrash={() => onMoveToTrash(p.id, p.name)}
                  onMove={(folderId) => moveMutation.mutate({ id: p.id, folderId })}
                  archivePending={archivePending || moveMutation.isPending}
                  moveDisabled={foldersUnavailable}
                />
              ))}
            </FolderSection>
          ))}

        <FolderSection
          dropId={DROP_UNCATEGORIZED}
          title={foldersUnavailable ? 'プロジェクト' : '未分類'}
          count={grouped.uncategorized.length}
          collapsed={!!collapsed[DROP_UNCATEGORIZED]}
          onToggle={() => toggle(DROP_UNCATEGORIZED)}
          onCreateHere={() => onCreateInFolder(null)}
        >
          {grouped.uncategorized.map((p) => (
            <ActiveProjectRow
              key={p.id}
              project={p}
              folders={folders}
              onMoveToTrash={() => onMoveToTrash(p.id, p.name)}
              onMove={(folderId) => moveMutation.mutate({ id: p.id, folderId })}
              archivePending={archivePending || moveMutation.isPending}
              moveDisabled={foldersUnavailable}
            />
          ))}
        </FolderSection>
      </div>

      <DeleteFolderModal
        folder={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (deleteTarget) deleteMutation.mutate(deleteTarget.id)
        }}
        isPending={deleteMutation.isPending}
      />
    </div>
  )
}

/** 単一場所（フォルダ／未分類）のフラット一覧 */
export function LocationProjectsList({
  projects,
  folders,
  foldersUnavailable,
  onMoveToTrash,
  archivePending,
}: {
  projects: Project[]
  folders: ProjectFolder[]
  foldersUnavailable: boolean
  onMoveToTrash: (id: string, name: string) => void
  archivePending: boolean
}) {
  const queryClient = useQueryClient()
  const toast = useToast()

  const moveMutation = useMutation({
    mutationFn: ({ id, folderId }: { id: string; folderId: string | null }) =>
      updateProject(id, { folderId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      queryClient.invalidateQueries({ queryKey: ['folders'] })
      toast.success('移動しました')
    },
    onError: (err) => toast.error(toUserMessage(err, SUBMIT_ERROR_FALLBACK)),
  })

  if (projects.length === 0) {
    return (
      <p className="px-1 py-10 text-center text-sm text-muted-foreground">
        この場所にプロジェクトはありません。ドラッグで移動するか、新規作成してください。
      </p>
    )
  }

  return (
    <ul className="flex flex-col divide-y divide-border border border-border rounded-md bg-card overflow-hidden">
      {projects.map((p) => (
        <ActiveProjectRow
          key={p.id}
          project={p}
          folders={folders}
          onMoveToTrash={() => onMoveToTrash(p.id, p.name)}
          onMove={(folderId) => moveMutation.mutate({ id: p.id, folderId })}
          archivePending={archivePending || moveMutation.isPending}
          moveDisabled={foldersUnavailable}
        />
      ))}
    </ul>
  )
}

function groupProjects(projects: Project[], folders: ProjectFolder[]) {
  const byFolder = new Map<string, Project[]>()
  for (const f of folders) byFolder.set(f.id, [])
  const uncategorized: Project[] = []
  for (const p of projects) {
    if (p.folderId && byFolder.has(p.folderId)) {
      byFolder.get(p.folderId)?.push(p)
    } else {
      uncategorized.push(p)
    }
  }
  return {
    folders: folders.map((folder) => ({ folder, items: byFolder.get(folder.id) ?? [] })),
    uncategorized,
  }
}

function FoldersUnavailableBanner({ error }: { error: unknown }) {
  const detail = toUserMessage(error, FETCH_ERROR_FALLBACK)
  const isMissingOnRemote =
    error instanceof ApiError &&
    (error.status === 404 ||
      error.status === 403 ||
      error.message.includes('管理者権限') ||
      error.message.includes('見つかりません'))
  return (
    <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 space-y-1">
      <p className="text-sm font-semibold text-foreground">フォルダ機能をこの API では使えません</p>
      <p className="text-xs text-muted-foreground leading-relaxed">
        {isMissingOnRemote
          ? 'ローカルの画面は本番 API（VITE_API_URL）を参照しています。フォルダ API は未デプロイのため、マイグレーション＋ローカル API 起動、またはステージングへのデプロイが必要です。'
          : `フォルダ一覧の取得に失敗しました（${detail}）。プロジェクト一覧のみ表示しています。`}
      </p>
    </div>
  )
}

function FolderToolbar({
  creating,
  newFolderName,
  folderCount,
  existingNames,
  nameError,
  onStartCreate,
  onCancelCreate,
  onChangeName,
  onSubmitCreate,
  createPending,
}: {
  creating: boolean
  newFolderName: string
  folderCount: number
  existingNames: string[]
  nameError: string | null
  onStartCreate: () => void
  onCancelCreate: () => void
  onChangeName: (v: string) => void
  onSubmitCreate: () => void
  createPending: boolean
}) {
  const normalized = normalizeFolderName(newFolderName)
  const isDuplicate =
    normalized.length > 0 && existingNames.some((n) => normalizeFolderName(n) === normalized)
  const inlineError = isDuplicate ? FOLDER_NAME_CONFLICT_MESSAGE : nameError
  const canSubmit = normalized.length > 0 && !isDuplicate && !createPending

  if (creating) {
    return (
      <div className="rounded-md border border-border bg-muted/40 p-3 space-y-2">
        <p className="text-sm font-semibold text-foreground">新しいフォルダ</p>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="text"
            maxLength={100}
            value={newFolderName}
            onChange={(e) => onChangeName(e.target.value)}
            placeholder="例: 1on1撮影、東京撮影"
            aria-invalid={inlineError ? true : undefined}
            className={`flex-1 min-w-[12rem] px-3 py-2 border rounded-md bg-background text-sm ${
              inlineError ? 'border-destructive' : 'border-border'
            }`}
            // biome-ignore lint/a11y/noAutofocus: インライン作成のメイン入力
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                if (!e.repeat && canSubmit) onSubmitCreate()
              }
              if (e.key === 'Escape') onCancelCreate()
            }}
          />
          <button
            type="button"
            onClick={onSubmitCreate}
            disabled={!canSubmit}
            className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 bg-foreground text-background rounded-md disabled:opacity-50"
          >
            {createPending ? '作成中…' : '作成する'}
          </button>
          <button
            type="button"
            onClick={onCancelCreate}
            disabled={createPending}
            className="text-sm font-medium px-3 py-2 border border-border rounded-md bg-card hover:bg-accent"
          >
            キャンセル
          </button>
        </div>
        {inlineError && <p className="text-xs text-destructive">{inlineError}</p>}
      </div>
    )
  }

  if (folderCount === 0) {
    return (
      <div className="rounded-md border border-dashed border-border bg-muted/30 px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground">フォルダで案件をまとめられます</p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            「1on1撮影」「東京撮影」のように箱を作り、プロジェクトをドラッグして仕分けできます。
          </p>
        </div>
        <button
          type="button"
          onClick={onStartCreate}
          className="inline-flex shrink-0 items-center justify-center gap-2 px-3.5 py-2 rounded-md text-sm font-medium border border-border bg-card hover:bg-accent transition-colors"
        >
          <FolderPlusIcon />
          フォルダを追加
        </button>
      </div>
    )
  }

  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <p className="text-xs text-muted-foreground">
        フォルダやサイドバーにドラッグして移動。または行の移動先から変更できます。
      </p>
      <button
        type="button"
        onClick={onStartCreate}
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium border border-border bg-card hover:bg-accent transition-colors"
      >
        <FolderPlusIcon />
        フォルダを追加
      </button>
    </div>
  )
}

function FolderPlusIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 20 20"
      fill="none"
      className="h-4 w-4"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M3 6.5A1.5 1.5 0 0 1 4.5 5H8l1.2 1.2H15.5A1.5 1.5 0 0 1 17 7.7v6.8A1.5 1.5 0 0 1 15.5 16h-11A1.5 1.5 0 0 1 3 14.5v-8Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path d="M10 9.5v4M8 11.5h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}

function FolderSection({
  dropId,
  title,
  count,
  collapsed,
  onToggle,
  onRename,
  onDelete,
  onCreateHere,
  children,
}: {
  dropId: string
  title: string
  count: number
  collapsed: boolean
  onToggle: () => void
  onRename?: () => void
  onDelete?: () => void
  onCreateHere: () => void
  children: React.ReactNode
}) {
  const { setNodeRef, isOver } = useDroppable({ id: dropId })
  return (
    <section
      ref={setNodeRef}
      className={`group/folder rounded-md overflow-hidden transition-colors ${
        isOver ? 'bg-primary/5 ring-1 ring-primary/30' : 'bg-transparent'
      }`}
    >
      <div className="flex items-center gap-1 px-1 py-1.5">
        <button
          type="button"
          onClick={onToggle}
          className="flex flex-1 items-center gap-2 min-w-0 text-left rounded-md px-1.5 py-1 hover:bg-muted/60"
          aria-expanded={!collapsed}
        >
          <span aria-hidden className="text-muted-foreground text-[10px] w-3">
            {collapsed ? '▸' : '▾'}
          </span>
          <span className="font-semibold text-[13px] truncate">{title}</span>
          <span className="text-[11px] text-muted-foreground editorial-num">{count}</span>
        </button>
        <button
          type="button"
          onClick={onCreateHere}
          className="text-[11px] text-muted-foreground hover:text-foreground px-1.5 py-1 opacity-100 sm:opacity-0 sm:group-hover/folder:opacity-100 focus:opacity-100"
        >
          ＋新規
        </button>
        {onRename && (
          <button
            type="button"
            onClick={onRename}
            className="text-[11px] text-muted-foreground hover:text-foreground px-1.5 py-1 opacity-100 sm:opacity-0 sm:group-hover/folder:opacity-100 focus:opacity-100"
          >
            改名
          </button>
        )}
        {onDelete && (
          <button
            type="button"
            onClick={onDelete}
            className="text-[11px] text-muted-foreground hover:text-destructive px-1.5 py-1 opacity-100 sm:opacity-0 sm:group-hover/folder:opacity-100 focus:opacity-100"
          >
            削除
          </button>
        )}
      </div>
      {!collapsed && (
        <ul className="flex flex-col divide-y divide-border/60 border border-border/80 rounded-md bg-card ml-2 mb-1 overflow-hidden">
          {children}
          {count === 0 && (
            <li className="px-4 py-5 text-xs text-muted-foreground text-center list-none">
              プロジェクトをここにドロップ、または「＋新規」から作成
            </li>
          )}
        </ul>
      )}
    </section>
  )
}

function ActiveProjectRow({
  project,
  folders,
  onMoveToTrash,
  onMove,
  archivePending,
  moveDisabled = false,
}: {
  project: Project
  folders: ProjectFolder[]
  onMoveToTrash: () => void
  onMove: (folderId: string | null) => void
  archivePending: boolean
  moveDisabled?: boolean
}) {
  const queryClient = useQueryClient()
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: project.id,
    disabled: moveDisabled,
    data: {
      type: 'project',
      name: project.name,
      folderId: project.folderId,
    },
  })
  const style = transform ? { transform: CSS.Translate.toString(transform) } : undefined

  const prefetchDetail = () => {
    queryClient.prefetchQuery({
      queryKey: ['projects', project.id],
      queryFn: () => getProject(project.id),
    })
    queryClient.prefetchQuery({
      queryKey: ['files', project.id],
      queryFn: () => listFiles(project.id),
    })
  }

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`group ${isDragging ? 'opacity-60 z-10 bg-card shadow-sm' : 'bg-card'}`}
    >
      <div className="flex flex-col md:flex-row md:items-center">
        <div className="flex items-stretch min-w-0 flex-1">
          {!moveDisabled && (
            <button
              type="button"
              className="px-2 text-muted-foreground/50 hover:text-muted-foreground cursor-grab active:cursor-grabbing touch-none"
              aria-label={`${project.name} をドラッグして移動`}
              {...listeners}
              {...attributes}
            >
              ⠿
            </button>
          )}
          <Link
            to={`/projects/${project.id}`}
            onMouseEnter={prefetchDetail}
            onFocus={prefetchDetail}
            className="block min-w-0 flex-1 py-2.5 px-2"
            aria-label={`${project.name} を開く`}
          >
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-sm font-semibold tracking-tight truncate text-foreground group-hover:text-primary transition-colors">
                {project.name}
              </h2>
              {project.status && (
                <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 border border-border rounded-full text-muted-foreground">
                  {project.status}
                </span>
              )}
            </div>
            <div className="mt-0.5 flex items-center gap-2 text-[11px] text-muted-foreground editorial-num flex-wrap">
              <span>更新 {formatDate(project.updatedAt)}</span>
              <span aria-hidden className="text-muted-foreground/40">
                ·
              </span>
              <span>作成 {formatDate(project.createdAt)}</span>
            </div>
          </Link>
        </div>
        <div className="flex items-center gap-1.5 px-3 pb-2.5 md:pb-0 md:pr-3 shrink-0 border-t border-border/60 md:border-t-0 pt-2 md:pt-0 opacity-100 md:opacity-0 md:group-hover:opacity-100 md:focus-within:opacity-100 transition-opacity">
          {!moveDisabled && (
            <MoveToSelect
              folders={folders}
              currentFolderId={project.folderId}
              disabled={archivePending}
              onSelect={onMove}
            />
          )}
          <button
            type="button"
            onClick={onMoveToTrash}
            disabled={archivePending}
            className="text-[11px] font-medium px-2 py-1 border border-border bg-card rounded-md hover:bg-accent transition-colors disabled:opacity-50"
          >
            削除
          </button>
        </div>
      </div>
    </li>
  )
}

function MoveToSelect({
  folders,
  currentFolderId,
  disabled,
  onSelect,
}: {
  folders: ProjectFolder[]
  currentFolderId: string | null
  disabled: boolean
  onSelect: (folderId: string | null) => void
}) {
  return (
    <label className="inline-flex items-center gap-1 text-xs text-muted-foreground">
      <span className="sr-only">移動先</span>
      <select
        className="text-[11px] border border-border rounded-md bg-card px-2 py-1 max-w-[9rem]"
        disabled={disabled}
        value={currentFolderId ?? ''}
        aria-label="移動先を選ぶ"
        onChange={(e) => {
          const v = e.target.value
          onSelect(v === '' ? null : v)
        }}
      >
        <option value="">未分類</option>
        {folders.map((f) => (
          <option key={f.id} value={f.id}>
            {f.name}
          </option>
        ))}
      </select>
    </label>
  )
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleDateString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
}
